import React, { useState, useMemo, useCallback, useEffect, } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { leaveService } from "../../api/LeaveService/leaveService";
import { SearchBar } from "../components/SearchBar";
import { fetchBranchesByHierarchy } from "../../store/slices/branchSlice";

const EmployeeLeaveTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // State
  const { list: allBranches = [] } = useSelector((state) => state.branch);
  const storedBranchId = localStorage.getItem("branchId");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

useEffect(() => {

    dispatch(fetchBranchesByHierarchy({ branchId: storedBranchId }));
  
}, [dispatch, storedBranchId]);

  const isMultipleBranches = allBranches.length > 1;

  const branchOptions = isMultipleBranches
    ? [
        { value: storedBranchId, label: "All Branch" }, 
        ...allBranches
          .filter((branch) => 
            branch.id !== "All Branch" && 
            branch.id !== storedBranchId && 
            !/all/i.test(branch.branchName || branch.name)
          )
          .map((branch) => ({
            value: branch.id,
            label: branch.branchName || branch.name,
          }))
      ]
    : [
        ...allBranches.map((branch) => ({
          value: branch.id,
          label: branch.branchName || branch.name,
        }))
      ];

  const filterSchema = [
    { key: "name", label: "Name", type: "text", placeholder: "Search Name..." },
    {
      key: "branch",
      label: "Branch",
      defaultValue: storedBranchId,
      type: "select",
      options: branchOptions,
    }
  ];

  // Draft filters for input typing/selection
  const [filters, setFilters] = useState(() => {
    return filterSchema.reduce((initialObj, field) => {
      initialObj[field.key] =
        field.defaultValue !== undefined ? field.defaultValue : "";
      return initialObj;
    }, {});
  });

  // Committed filters that actually trigger network requests
  const [appliedFilters, setAppliedFilters] = useState(filters);

  const queryKey = ["getAllEmployeeBalance", { ...appliedFilters, currentPage, itemsPerPage }];

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => leaveService.getAllEmployeeBalance({
      ...appliedFilters,
     branchId: appliedFilters.branchId || storedBranchId,
      page: currentPage,
      limit: itemsPerPage
    }),
    keepPreviousData: true,
  });

  const employeeListData = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    return data?.list || data?.data || [];
  }, [data]);

  const serverMeta = useMemo(() => {
    if (!data) return { total: 0, totalPages: 1, page: 1 };

    const rawTotal = 
      data.total ?? 
      data.meta?.total ?? 
      data.count ?? 
      data.pagination?.total;

    const total = rawTotal !== undefined ? rawTotal : (Array.isArray(data) ? data.length : 0);

    const totalPages = 
      data.totalPages ?? 
      data.meta?.totalPages ?? 
      data.pagination?.totalPages ?? 
      (Math.ceil(total / itemsPerPage) || 1);

    return {
      total: Number(total),
      totalPages: Number(totalPages) < currentPage ? currentPage : Number(totalPages),
      page: currentPage,
    };
  }, [data, itemsPerPage, currentPage]);

  const totalPages = serverMeta.totalPages;
  const totalItems = serverMeta.total;

  const getPaginationGroup = () => {
    const totalDisplayed = 5;
    let start = Math.max(currentPage - Math.floor(totalDisplayed / 2), 1);
    let end = Math.min(start + totalDisplayed - 1, totalPages);
    if (end === totalPages) start = Math.max(end - totalDisplayed + 1, 1);
    const pages = [];
    for (let i = start; i <= end; i++) if (i > 0) pages.push(i);
    return pages.filter((p) => p !== 1 && p !== totalPages);
  };

  const handleSearch = () => {
    const cleanFilters = {};

    Object.keys(filters).forEach((key) => {
      const value = filters[key];

      if (
        value !== undefined &&
        value !== null &&
        value !== "All" &&
        value !== "both" &&
        String(value).trim() !== ""
      ) {
        const trimmedValue = String(value).trim();

        if (key === "branch") {
          cleanFilters.branchId = trimmedValue;
        } else {
          cleanFilters[key] = trimmedValue;
        }
      }
    });

    
    setCurrentPage(1);
    // Committing the filters triggers the useQuery hook execution
    setAppliedFilters(cleanFilters);
  };

  const handlePageChange = useCallback(
    (targetPage, overrideLimit = null) => {
      setCurrentPage(targetPage);

      if (overrideLimit !== null && overrideLimit !== itemsPerPage) {
        setItemsPerPage(overrideLimit);
      }
    },
    [itemsPerPage]
  );

  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error loading data.</div>;

  return (
    <div className="w-100">
      <style>{`
        .custom-table-header th { 
          background-color: #1e293b !important; color: #ffffff !important; 
          cursor: pointer; user-select: none; transition: background 0.2s;
          font-size: 0.7rem !important; font-weight: 700 !important; 
          letter-spacing: 0.05em !important; text-transform: uppercase !important;
        }
        .custom-table-header th:hover { background-color: #334155 !important; }
        .emp-name-text { font-size: 0.85rem !important; font-weight: 700 !important; color: #1e293b; }
        .emp-sub-text { font-size: 0.75rem !important; color: #64748b; }
        .table-cell-text { font-size: 0.8rem !important; color: #334155; }
        .badge-custom { 
          font-size: 0.65rem !important; font-weight: 700 !important; 
          letter-spacing: 0.02em !important; padding: 6px 12px !important;
        }
        .pagination-container { background: rgba(255, 255, 255, 0.4); border-top: 1px solid rgba(0,0,0,0.05); padding: 1rem 1.5rem; }
        .page-link-custom { width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; border-radius: 8px !important; margin: 0 3px; font-size: 0.75rem; font-weight: 600; color: #64748b; cursor: pointer; background: transparent; transition: all 0.2s; border: 1px solid rgba(0,0,0,0.1); }
        .page-link-custom.active { background: #0f172a !important; color: #fff !important; border-color: #0f172a !important; }
        .filter-input, .status-select { border: 1px solid rgba(0,0,0,0.1); border-radius: 8px; font-size: 0.8rem !important; padding: 10px 14px; background: rgba(255,255,255,0.7); backdrop-filter: blur(5px); outline: none; }
        .items-select { border: 1px solid rgba(0,0,0,0.1); border-radius: 6px; padding: 4px 8px; outline: none; color: #334155; font-weight: 500; }
      `}</style>
      <h1 className="fw-bold text-dark">Employees Leave Management</h1>
      
       <SearchBar
          filters={filters}
          setFilters={setFilters}
          fields={filterSchema}
          onSearch={handleSearch}
       />

      <table className="table">
        <thead>
          <tr>
            <th>Employee Leave Details</th>
          </tr>
        </thead>
        <tbody>
          {employeeListData.map((emp) => {
            const leaveBalances = Array.isArray(emp.leaveBalance) 
              ? emp.leaveBalance 
              : emp.leaveBalance ? [emp.leaveBalance] : [];

            return (
              <tr key={emp.id}>
                <td colSpan="5" className="p-2 border-0">
                  <button
                    type="button"
                    className="w-100 text-start btn btn-outline-light border text-dark p-3 shadow-sm d-flex justify-content-between align-items-center bg-white"
                    onClick={() => navigate(`/leave/employee/${emp.id}`)}
                    style={{ transition: "all 0.2s ease-in-out" }}
                  >
                    <div className="d-flex flex-column gap-1">
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <h5 className="mb-0 fw-bold text-primary">
                          {emp.firstName || ""} {emp.middleName ? `${emp.middleName[0]}. ` : ""}{emp.lastName || ""} {emp.extension || ""}
                        </h5>
                        <span className="badge bg-secondary text-white font-monospace small">
                          {emp.employeeId || "N/A"}
                        </span>
                        <span className={`badge ${emp.employeeStatus === "Validated" ? "bg-success" : "bg-warning"} text-white`}>
                          {emp.employeeStatus || "Unassigned"}
                        </span>
                      </div>

                      <div className="text-muted small d-flex flex-wrap gap-3 mt-1">
                        <span><strong>Branch:</strong> {emp.branch?.branchName || "Unassigned"}</span>
                        <span><strong>Email:</strong> {emp.companyEmail || emp.personalEmail || "N/A"}</span>
                      </div>
                    </div>

                    <div className="d-flex align-items-center gap-4">
                      <div className="text-end border-start ps-3" style={{ minWidth: "320px" }}>
                        <span className="d-block small text-muted fw-semibold mb-1 text-center">
                          Leave Balances
                        </span>
                        {leaveBalances.length > 0 ? (
                          <div className="d-flex flex-column gap-1">
                            {leaveBalances.map((lb, index) => (
                              <div 
                                key={lb.id || index} 
                                className="small bg-light px-2 py-1 rounded border d-flex justify-content-between align-items-center"
                              >
                                <span className="fw-bold text-dark text-truncate" style={{ maxWidth: "110px" }} title={lb.leaveType?.name || "Leave"}>
                                  {lb.leaveType?.name || "Leave"}:
                                </span>
                                <div className="d-flex gap-2">
                                  <span className="text-success">Alloc: <strong>{lb.allocated ?? "0"}</strong></span>
                                  <span className="text-success">Booked: <strong>{lb.booked ?? "0"}</strong></span>
                                  <span className="text-danger">Used: <strong>{lb.used ?? "0"}</strong></span>
                                  <span className="text-primary">Pend: <strong>{lb.pending ?? "0"}</strong></span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="small text-muted fst-italic text-center py-2 bg-light rounded border">
                            No leave balance data
                          </div>
                        )}
                      </div>

                      <div className="btn btn-sm btn-primary px-3">
                        View &rarr;
                      </div>
                    </div>
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="pagination-container d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 p-3">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted" style={{ fontSize: "0.75rem" }}>
            Show
          </span>
          <select
            className="items-select"
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            {[10, 25, 50].map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
          <span className="text-muted" style={{ fontSize: "0.75rem" }}>
            of <strong>{(totalItems || 0).toLocaleString()}</strong> entries
          </span>
        </div>

        <nav className="d-flex align-items-center">
          <button
            type="button"
            className="page-link-custom"
            onClick={() => {
              const prevNum = Number(currentPage) - 1;
              if (prevNum >= 1) handlePageChange(prevNum);
            }}
            disabled={Number(currentPage) <= 1}
            style={{
              opacity: Number(currentPage) <= 1 ? 0.35 : 1,
              cursor: Number(currentPage) <= 1 ? "not-allowed" : "pointer",
            }}
          >
            <i className="bi bi-chevron-left" style={{ fontSize: "0.8rem", stroke: "#64748b", strokeWidth: "1.5px" }}></i>
          </button>

          <button
            type="button"
            className={`page-link-custom ${Number(currentPage) === 1 ? "active" : ""}`}
            onClick={() => handlePageChange(1)}
          >
            1
          </button>

          {Number(currentPage) > 3 && totalPages > 5 && (
            <span className="mx-2 text-muted">...</span>
          )}

          {getPaginationGroup().map((p) => {
            if (p === 1 || p === totalPages) return null;
            return (
              <button
                type="button"
                key={p}
                className={`page-link-custom ${Number(currentPage) === p ? "active" : ""}`}
                onClick={() => handlePageChange(p)}
              >
                {p}
              </button>
            );
          })}

          {Number(currentPage) < totalPages - 2 && totalPages > 5 && (
            <span className="mx-2 text-muted">...</span>
          )}

          {totalPages > 1 && (
            <button
              type="button"
              className={`page-link-custom ${Number(currentPage) === totalPages ? "active" : ""}`}
              onClick={() => handlePageChange(totalPages)}
            >
              {totalPages}
            </button>
          )}

          <button
            type="button"
            className="page-link-custom"
            onClick={() => {
              const nextNum = Number(currentPage) + 1;
              if (nextNum <= totalPages) handlePageChange(nextNum);
            }}
            disabled={Number(currentPage) >= totalPages}
            style={{
              opacity: Number(currentPage) >= totalPages ? 0.35 : 1,
              cursor: Number(currentPage) >= totalPages ? "not-allowed" : "pointer",
            }}
          >
            <i className="bi bi-chevron-right" style={{ fontSize: "0.8rem", stroke: "#64748b", strokeWidth: "1.5px" }}></i>
          </button>
        </nav>
      </div>
    </div>
  );
};

export default EmployeeLeaveTable;